import { useMemo, type CSSProperties } from 'react';
import { useLazyInView } from '../hooks/useLazyInView';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useTokens,
  usePropertyField,
  RichTextContent,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { contrastRatio, getContrastTextColor } from '../utils/luminance';
import { FixedWaveTexture } from './_fixedWaveTexture';

// ─── Slot group definitions (inline literals — required by the schema linter) ──

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'Explore Neighborhood' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ─── Google Maps embed ─────────────────────────────────────────────────────────

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.GOOGLE_MAPS_API_KEY) || '';

// ─── Component ────────────────────────────────────────────────────────────────

export function PinnedCallouts() {
  useRevealStyles();
  const revealRef = useScrollReveal<HTMLDivElement>();

  const tokens = useTokens();

  // The body copy sits over the `~88% secondary` muting overlay, so it
  // effectively reads against `--vibe-color-secondary`. Keep the themed text
  // when it clears WCAG AA against secondary; otherwise fall back to a
  // luminance-picked black/white.
  const bodyTextStyle = useMemo<CSSProperties | undefined>(() => {
    const secondary = tokens?.color?.secondary;
    if (!secondary) return undefined;
    const themed = tokens?.color?.text;
    const color = themed && contrastRatio(secondary, themed) >= 4.5 ? themed : getContrastTextColor(secondary);
    return { ['--pc-body-text' as string]: color } as CSSProperties;
  }, [tokens]);

  const cta = useSlotGroup('pinnedcallouts.cta', ctaDef);
  const [mapRef, mapVisible] = useLazyInView<HTMLDivElement>();

  // Property-centred map — mirrors ContactMap's embed logic.
  const lat = usePropertyField('property_address_latitude') as string | null;
  const lng = usePropertyField('property_address_longitude') as string | null;
  const address = usePropertyField('property_full_address') as string | null;

  const hasCoords = !!lat && !!lng && lat.trim() !== '' && lng.trim() !== '';

  let mapSrc = '';
  if (hasCoords && MAPS_API_KEY) {
    mapSrc = `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${lat},${lng}&zoom=14`;
  } else if (hasCoords) {
    mapSrc = `https://maps.google.com/maps?q=${lat},${lng}&z=14&output=embed`;
  } else if (address && address.trim() !== '') {
    mapSrc = `https://maps.google.com/maps?q=${encodeURIComponent(address.trim())}&output=embed`;
  }

  return (
    <section className="relative w-full overflow-hidden">
      {/* ── Fixed wave texture (z-0) + muting overlay (z-[1]) ── */}
      <FixedWaveTexture />

      {/* ── Content (z-10) ── */}
      <div
        ref={revealRef}
        style={bodyTextStyle}
        className="reveal-fade-up relative z-10 grid grid-cols-1 lg:grid-cols-2 items-stretch gap-8 md:gap-12 lg:gap-0 py-12 md:py-16 lg:py-12 xl:py-16"
      >
        {/* Map column (full bleed to the left edge).
            Stacked layouts keep it below the copy; the split from 1024px
            puts it back on the left. Min-height is a floor — the row still
            grows with the copy — and stays shorter until the column is wide. */}
        <div
          ref={mapRef}
          className="order-2 lg:order-1 relative w-full min-w-0 h-full overflow-hidden min-h-[300px] sm:min-h-[360px] md:min-h-[420px] lg:min-h-[440px] xl:min-h-[520px] bg-[var(--vibe-color-secondary)]"
        >
          {mapSrc && mapVisible ? (
            <iframe
              className="absolute inset-0 w-full h-full border-0"
              src={mapSrc}
              title="Property location map"
              loading="lazy"
              allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
            />
          ) : mapSrc ? null : (
            <div className="absolute inset-0 flex items-center justify-center">
              <p className="font-body text-sm text-[var(--vibe-color-text-muted)]">
                Map location not available
              </p>
            </div>
          )}
        </div>

        {/* Text column — heading/copy/CTA. Sits above the map while stacked,
            and in the right column once the split starts at 1024px. */}
        <div className="order-1 lg:order-2 flex min-w-0 flex-col justify-center px-4 sm:px-6 md:px-10 lg:px-8 xl:px-12">
          <div className="pc-copy w-full max-w-2xl mx-auto md:max-w-3xl lg:ml-0 lg:mr-auto lg:max-w-none xl:max-w-2xl">
            <RichTextContent
              path="pinnedcallouts.heading"
              label="Heading"
              defaultValue="<p>Find Your Island Escape</p>"
              as="h2"
              className="pc-heading font-heading font-bold text-white leading-[1.05] uppercase mb-4 lg:mb-3 xl:mb-5"
            />

            <RichTextContent
              path="pinnedcallouts.body"
              label="Body Text"
              defaultValue="<p>Perfectly positioned near the best of the neighborhood, our community puts dining, shopping, and entertainment just minutes from your front door. Whether you're commuting to work or exploring the city, everything you need is within easy reach.</p>"
              as="div"
              className="font-body text-base text-[color:var(--pc-body-text,var(--vibe-color-text))] leading-relaxed [&_a]:underline mb-6 lg:mb-5 xl:mb-6"
            />

            <EditableSlotGroup groupPath="pinnedcallouts.cta" definition={ctaDef}>
              <a
                href={cta.url || '/neighborhood'}
                target={cta._target || '_self'}
                className="pc-cta inline-flex max-w-full items-center justify-center whitespace-nowrap font-heading uppercase transition-all"
              >
                {cta.label || 'Explore Neighborhood'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>

      <style>{`
        /* Heading tracks the text column. Full-width tablet can carry a
           larger line; the 1024px split sizes off the column (cqi) so a
           13" laptop doesn't get the wide-desktop headline. */
        .pc-copy {
          container-type: inline-size;
        }
        .pc-heading {
          font-size: clamp(1.75rem, 7.5vw, 2.25rem);
        }

        /* Solid pill CTA — filled with the theme primary, matching the
           reference stripe's "Explore Neighborhood" button. Padding stays
           in this stylesheet so it can shrink with the column. */
        .pc-cta {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary, #fff);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 9999px;
          padding: 0.75rem 1.5rem;
          font-size: 0.875rem;
          letter-spacing: 0.12em;
        }
        .pc-cta:hover {
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          border-color: color-mix(in srgb, var(--vibe-color-primary) 88%, #000);
          transform: translateY(-1px);
          box-shadow: 0 6px 18px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
        }

        @media (min-width: 768px) {
          .pc-heading {
            font-size: clamp(2.15rem, 4.2vw, 3rem);
          }
          .pc-cta {
            padding: 0.875rem 2rem;
            letter-spacing: 0.14em;
          }
        }
        @media (min-width: 1024px) {
          .pc-heading {
            font-size: clamp(1.9rem, 7.2cqi, 3rem);
          }
          .pc-cta {
            padding: 0.7rem 1.25rem;
            letter-spacing: 0.08em;
          }
        }
        @media (min-width: 1280px) {
          .pc-heading {
            font-size: clamp(2.15rem, 7.5cqi, 3.5rem);
          }
          .pc-cta {
            padding: 0.9rem 2.25rem;
            font-size: 1rem;
            letter-spacing: 0.14em;
          }
        }

        /* A long label in the narrowest split column wraps instead of
           spilling into the map. */
        @container (max-width: 420px) {
          .pc-cta {
            white-space: normal;
            text-align: center;
          }
        }
      `}</style>
    </section>
  );
}

// ─── Content Schema ───────────────────────────────────────────────────────────

export const contentSchema: ComponentContentSchema = {
  slot: 'PinnedCallouts',
  displayName: 'Pinned Callouts',
  tokens: {
    'pinnedcallouts.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Find Your Island Escape</p>',
    },
    'pinnedcallouts.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_neighborhood_blurb',
      defaultValue:
        "<p>Perfectly positioned near the best of the neighborhood, our community puts dining, shopping, and entertainment just minutes from your front door. Whether you're commuting to work or exploring the city, everything you need is within easy reach.</p>",
    },
  },
  slotGroups: {
    'pinnedcallouts.cta': ctaDef,
  },
};

(PinnedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PinnedCallouts;
